import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Context as AppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import { capitalizeFirstWord } from '../../utils/functions';
import { exportInternshipsToCSV, exportUnmatchedInternsToCSV } from '../../utils/utils';
import MatchInternModal from './MatchInternModal';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from "react-toastify";
import useConfirm from '../../hooks/useConfirm';

const AdminHomepage = () => {
    const {
        state: { currentUser, interns, internships, mentors },
        getAllInternships,
        getAllInterns,
        getAllMentors,
        deleteInternship,
        updateSomeUserData
    } = useContext(AppContext);
    const navigate = useNavigate();
    const [matchModalOpen, setMatchModalOpen] = useState(false);
    const [selectedIntern, setSelectedIntern] = useState(null);

    // Setup confirmation dialog
    const [ConfirmationDialog, confirm] = useConfirm(
        'Cancel Internship',
        'Are you sure you want to cancel this internship? This action cannot be undone and will permanently remove the internship from the system.'
    );

    useEffect(() => {
        if (currentUser) {
            // Check if user is admin
            if (currentUser.role !== 'ADMIN') {
                toast.error('Access denied. Admin privileges required.');
                navigate('/');
                return;
            }

            // Admin queries to get all data
            getAllInternships();
            getAllInterns();
            getAllMentors();
        }
    }, [currentUser, navigate]);

    const cancelInternship = async (internshipUid) => {
        const isConfirmed = await confirm();
        if (isConfirmed) {
            console.log(internshipUid);
            deleteInternship(internshipUid);
            toast.success('Internship cancelled successfully');
        }
    };
    // Local method to filter unmatched interns
    const getUnmatchedInterns = () => {
        if (!interns) return [];
        console.log(interns);
        return interns.filter(intern =>   
            (!intern.internshipUid || intern.internshipUid === '')
        );
    };

    const unmatchedInterns = getUnmatchedInterns();
    console.log(unmatchedInterns);

    const getInternName = (studentUid) => {
        const intern = interns?.find(intern => intern.uid === studentUid);
        if (!intern) return 'Unknown Student';

        // Check if firstName/lastName exist, otherwise use name field
        if (intern.firstName || intern.lastName) {
            const firstName = capitalizeFirstWord(intern.firstName || '');
            const lastName = capitalizeFirstWord(intern.lastName || '');
            return `${firstName} ${lastName}`.trim() || 'Unknown Student';
        } else {
            return capitalizeFirstWord(intern.name || 'Unknown Student');
        }
    };

    const getMentorName = (mentorUid) => {
        const mentor = mentors?.find(mentor => mentor.uid === mentorUid);
        if (!mentor) return 'Unknown Mentor';

        // Check if firstName/lastName exist, otherwise use name field
        if (mentor.firstName || mentor.lastName) {
            const firstName = capitalizeFirstWord(mentor.firstName || '');
            const lastName = capitalizeFirstWord(mentor.lastName || '');
            return `${firstName} ${lastName}`.trim() || 'Unknown Mentor';
        } else {
            return capitalizeFirstWord(mentor.name || 'Unknown Mentor');
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Not set';
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getStatusColor = (startDate, endDate) => {
        const now = Date.now();
        const start = startDate ? new Date(startDate).getTime() : null;
        const end = endDate ? new Date(endDate).getTime() : null;
        
        if (!start) return 'default';
        if (now < start) return 'warning'; // Not started
        if (end && now > end) return 'error'; // Ended
        return 'success'; // Active
    };

    const getStatusText = (startDate, endDate) => {
        const now = Date.now();
        const start = startDate ? new Date(startDate).getTime() : null;
        const end = endDate ? new Date(endDate).getTime() : null;
        
        if (!start) return 'Not Started';
        if (now < start) return 'Upcoming';
        if (end && now > end) return 'Completed';
        return 'Active';
    };

    const handleViewInternship = (internshipUid) => {
        navigate(`/internship/${internshipUid}`);
    };

    const handleOpenMatchModal = (intern) => {
        setSelectedIntern(intern);
        setMatchModalOpen(true);
    };

    const handleCloseMatchModal = () => {
        setMatchModalOpen(false);
        setSelectedIntern(null);
    };

    const handleExportToCSV = () => {
        if (!internships || internships.length === 0) {
            toast.warning('No internships to export');
            return;
        }
        exportInternshipsToCSV(internships, interns, mentors);
        toast.success('Internships exported successfully');
    };

    const handleExportUnmatchedToCSV = () => {
        if (!unmatchedInterns || unmatchedInterns.length === 0) {
            toast.warning('No unmatched interns to export');
            return;
        }
        exportUnmatchedInternsToCSV(unmatchedInterns, mentors);
        toast.success('Unmatched interns exported successfully');
    };

    const handleDeactivateMentor = async (mentorUid) => {
        try {
            await updateSomeUserData({
                uid: mentorUid,
                isMentor: false
            });
            toast.success('Mentor deactivated successfully');
            // Refresh mentors list
            getAllMentors();
        } catch (error) {
            console.error('Error deactivating mentor:', error);
            toast.error('Failed to deactivate mentor');
        }
    };

    // Prevent rendering if user is not admin
    if (!currentUser || currentUser.role !== 'ADMIN') {
        return (
            <MainLayout>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '60vh'
                }}>
                    <Typography variant="h5" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            </MainLayout>
        );
    }

    console.log('internships');
    console.log(internships);
    return (
        <MainLayout>
            <Stack gap={3} sx={{ 
                pt: 3,
                px: 3,
                pb: 3,
                overflow: 'auto'
            }}>
                <Typography variant={'h3'}>
                    Admin Dashboard - 
                    <span className={'rainbow-text'}> {capitalizeFirstWord(currentUser?.name)} </span>
                </Typography>

                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography variant="h4">
                            All Internships ({internships?.length || 0})
                        </Typography>
                        {internships && internships.length > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportToCSV}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b4299 100%)',
                                    }
                                }}
                            >
                                Export as CSV
                            </Button>
                        )}
                    </Stack>

                    {!internships || internships.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary">
                                No internships found
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                Check the Applicants section to start accepting students
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/applicants')}
                                sx={{
                                    mt: 2,
                                    borderRadius: '8px',
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    border: '1px solid',
                                    borderColor: 'rgba(139, 90, 43, 0.3)',
                                    color: '#8B5A2B',
                                    bgcolor: 'rgba(139, 90, 43, 0.02)',
                                    '&:hover': {
                                        borderColor: 'rgba(139, 90, 43, 0.5)',
                                        bgcolor: 'rgba(139, 90, 43, 0.05)',
                                        boxShadow: 'none'
                                    },
                                    transition: 'all 0.15s ease',
                                    minWidth: 'auto'
                                }}
                            >
                                View Applicants
                            </Button>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="internships table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><strong>Internship Title</strong></TableCell>
                                        <TableCell><strong>Mentor</strong></TableCell>
                                        <TableCell><strong>Intern</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Start Date</strong></TableCell>
                                        <TableCell><strong>End Date</strong></TableCell>
                                        <TableCell><strong>Tasks/Goals</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody>
                                    {internships?.map((internship, index) => (
                                        <TableRow 
                                            key={internship.uid}
                                            sx={{ 
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': { backgroundColor: '#f9f9f9' }
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                <Typography variant="body1" fontWeight="medium">
                                                    {internship.title || `Internship ${index + 1}`} - {internship.uid}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {getMentorName(internship.mentorUid)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {getInternName(internship.studentUid)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusText(internship.startDate, internship.endDate)}
                                                    color={getStatusColor(internship.startDate, internship.endDate)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(internship.startDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(internship.endDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    T:{internship.tasks?.length || 0} G:{internship.goals?.length || 0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => handleViewInternship(internship.uid)}
                                                    sx={{ minWidth: '90px' }}
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<CancelIcon />}
                                                    onClick={() => cancelInternship(internship.uid)}
                                                    sx={{ minWidth: '90px' }}
                                                >
                                                    Cancel Internship
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>

                {/* Unmatched Interns Section */}
                <Box sx={{ mt: 5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Box>
                            <Typography variant="h4">
                                Unmatched Interns ({unmatchedInterns?.length || 0})
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                These interns have applied to mentors but haven't been assigned to an internship yet.
                            </Typography>
                        </Box>
                        {unmatchedInterns && unmatchedInterns.length > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportUnmatchedToCSV}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    bgcolor: '#ff9800',
                                    '&:hover': {
                                        bgcolor: '#f57c00'
                                    }
                                }}
                            >
                                Export as CSV
                            </Button>
                        )}
                    </Stack>
                    
                    {!unmatchedInterns || unmatchedInterns.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary">
                                No unmatched interns found
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                All interns who have applied have been matched with mentors
                            </Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="unmatched interns table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                                        <TableCell><strong>Intern Name</strong></TableCell>
                                        <TableCell><strong>School</strong></TableCell>
                                        <TableCell><strong>Location</strong></TableCell>
                                        <TableCell><strong>Interests</strong></TableCell>
                                        <TableCell><strong>Applications</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {unmatchedInterns?.map((intern) => (
                                        <TableRow 
                                            key={intern.uid}
                                            sx={{ 
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': { backgroundColor: '#fff8f0' }
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                <Typography variant="body1" fontWeight="medium">
                                                    {intern.firstName || intern.lastName
                                                        ? `${capitalizeFirstWord(intern.firstName || '')} ${capitalizeFirstWord(intern.lastName || '')}`.trim()
                                                        : capitalizeFirstWord(intern.name || 'Unknown')
                                                    }
                                                </Typography>
                                                {intern.jobTitle && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {intern.jobTitle}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {intern.school || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {intern.location || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {intern.interests || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${intern.mentorApplications?.length || 0} applications`}
                                                    color="warning"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleOpenMatchModal(intern)}
                                                    sx={{ minWidth: '120px' }}
                                                >
                                                    Manage Match
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>

                {/* All Mentors Section */}
                <Box sx={{ mt: 5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Box>
                            <Typography variant="h4">
                                All Mentors ({mentors?.length || 0})
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                Manage all mentors in the system. Deactivate mentors to remove their access.
                            </Typography>
                        </Box>
                    </Stack>

                    {!mentors || mentors.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary">
                                No mentors found
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                There are currently no mentors in the system
                            </Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="mentors table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                        <TableCell><strong>Mentor Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Company</strong></TableCell>
                                        <TableCell><strong>Job Title</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mentors?.map((mentor) => (
                                        <TableRow
                                            key={mentor.uid}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': { backgroundColor: '#f0f7ff' }
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                <Typography variant="body1" fontWeight="medium">
                                                    {mentor.firstName || mentor.lastName
                                                        ? `${capitalizeFirstWord(mentor.firstName || '')} ${capitalizeFirstWord(mentor.lastName || '')}`.trim()
                                                        : capitalizeFirstWord(mentor.name || 'Unknown')
                                                    }
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {mentor.email || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {mentor.company || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {mentor.jobTitle || 'Not specified'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={mentor.isMentor !== false ? 'Active' : 'Inactive'}
                                                    color={mentor.isMentor !== false ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {mentor.isMentor !== false && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeactivateMentor(mentor.uid)}
                                                        sx={{ minWidth: '100px' }}
                                                    >
                                                        Deactivate
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Stack>

            <MatchInternModal
                open={matchModalOpen}
                onClose={handleCloseMatchModal}
                intern={selectedIntern}
            />
            <ConfirmationDialog />
        </MainLayout>
    );
};

export default AdminHomepage;
